import { defineStore } from 'pinia'
import Network from '@/Network'
import { getApiErrorMessage } from '@/utils/apiError'
import {
  ActivityType,
  ContainerKind,
  DSAttendanceStatus,
  hasRulesAndTemplates
} from '@/constants/ds-attendance.constants'
import { createDefaultRegistrationSettings } from '@/Network/Services/DsAttendanceService'
import type {
  ActivityRecordCreatedResponse,
  ContainerCountsByKind,
  DSAttendanceHealthUnitFilterOption,
  DSAttendanceScheduleFilterOption,
  EffectivePreviewEnvelope,
  RegistrationSettings
} from '@/Network/Services/DsAttendanceService'
import {
  DEFAULT_AUTO_APPROVAL,
  DEFAULT_MANUAL_REVIEW_SAMPLE_RATE,
  DEFAULT_SHEET_CONFERENCE_BY
} from '@/constants/ds-attendance-auto-approval.constants'
import {
  ListContainersByCompanyParams,
  DSAttendanceContainer,
  DSAttendancePolicyData
} from '@/types/ds-attendance.types'

/**
 * Rules são exigidas apenas quando a modalidade de monitoramento usa folhas
 * (FOLHA/EXTERNAL_APP). Para MINHA_ESCALA_APP enviamos array vazio para evitar
 * que rules placeholder (recurrence=null, shiftIds=[]) batam no NOT NULL do BE.
 */
function sanitizePolicyRules(policyData: DSAttendancePolicyData) {
  if (!hasRulesAndTemplates(policyData.monitoringType)) return []

  return (policyData.rules ?? [])
    .filter((rule) => rule.recurrence && (rule.shiftIds?.length ?? 0) > 0)
    .map((rule) => ({
      name: rule.name,
      shiftIds: (rule.shiftIds ?? []).map((id) => Number(id)),
      recurrence: rule.recurrence,
      dayOfMonth: rule.dayOfMonth,
      daysOfWeek: rule.daysOfWeek ?? []
    }))
}

function buildPolicySettings(policyData: DSAttendancePolicyData) {
  return {
    checkinLateToleranceMinutes: policyData.checkinLateToleranceMinutes,
    checkoutEarlyToleranceMinutes: policyData.checkoutEarlyToleranceMinutes,
    submissionDeadlineHours: policyData.submissionDeadlineHours,
    deadlineCalculationType: policyData.deadlineCalculationType,
    autoPermanenceOurTeam: policyData.autoPermanenceOurTeam ?? true,
    autoPermanenceNotOurTeam: policyData.autoPermanenceNotOurTeam ?? true,
    // Fase 2 do fluxo de auditoria (spec 2026-09-11, §6.1) — seção "Auditoria", no mesmo
    // `settings` das tolerâncias. Os padrões vão explícitos: uma política que nunca abriu a
    // seção precisa nascer com aprovação automática desligada no backend também.
    sheetConferenceBy: policyData.sheetConferenceBy ?? DEFAULT_SHEET_CONFERENCE_BY,
    autoApproval: policyData.autoApproval ?? DEFAULT_AUTO_APPROVAL,
    manualReviewSampleRate: policyData.manualReviewSampleRate ?? DEFAULT_MANUAL_REVIEW_SAMPLE_RATE,
    // F3 (spec 2026-09-12, §1.5/§6.2) — os três prazos da solicitação automática moram
    // DENTRO de `settings`: é `DSAttendancePolicySettingsDto` que os declara, e o
    // repositório do backend lê `data.settings?.requestAfter*`. No topo do payload eles
    // eram descartados em silêncio pelo `ValidationPipe({ whitelist: true })` global e
    // nenhum prazo era gravado.
    ...buildActivityRequestDelays(policyData)
  }
}

/**
 * F3 (spec 2026-09-12, §6.2) — serialização do prazo de um canal da solicitação
 * automática. `null` = usar o default do produto; `0` = NÃO cobrar neste canal. São
 * coisas DIFERENTES, e o payload precisa distinguir as duas: campo nunca tocado
 * permanece `null`; campo limpo pelo usuário vira `0` (a seção da política converte o
 * input vazio em `0` depois da confirmação do balão ③ de M4).
 *
 * O que NUNCA pode sair daqui é `undefined`: o backend leria "não mudou" e o usuário
 * ficaria sem conseguir desligar um canal que já estava ligado.
 */
const serializeRequestDelay = (value: number | null | undefined): number | null =>
  value === undefined ? null : value

/** Os três prazos, sempre presentes no payload (ver `serializeRequestDelay`). */
function buildActivityRequestDelays(policyData: DSAttendancePolicyData) {
  return {
    requestAfterCheckoutHoursApp: serializeRequestDelay(policyData.requestAfterCheckoutHoursApp),
    requestAfterDaysSheet: serializeRequestDelay(policyData.requestAfterDaysSheet),
    requestAfterHoursExternal: serializeRequestDelay(policyData.requestAfterHoursExternal),
    // Q18 — mesma regra dos prazos: sempre presentes, `null` = padrão do produto.
    activityRequestSendWindowHoursApp: serializeRequestDelay(
      policyData.activityRequestSendWindowHoursApp
    ),
    activityRequestSendWindowDaysSheet: serializeRequestDelay(
      policyData.activityRequestSendWindowDaysSheet
    )
  }
}

/**
 * F4 (spec 2026-09-12, §7.2b) — gate do pós-plantão, no TOPO do payload, onde
 * `Create/UpdateDSAttendancePolicyDto` o declara (ao lado de
 * `activityRecordsEnabled`), nunca dentro de `settings`.
 *
 * Diferente dos demais campos da seção, este é OMITIDO quando ausente em vez de
 * cair num default: só o formulário sabe se o backend expôs o campo, e mandar
 * `false` para um backend anterior à F4 seria descartado em silêncio pelo
 * `ValidationPipe`. `false` explícito continua viajando — é assim que se
 * DESLIGA um gate que o backfill ligou.
 */
function buildPostStationGate(policyData: DSAttendancePolicyData) {
  const value = policyData.postStationBlockOnRegistration
  return value === undefined || value === null ? {} : { postStationBlockOnRegistration: value }
}

/**
 * Mapeamento explícito para o payload do backend
 */
function buildSavePolicyPayload(policyData: DSAttendancePolicyData, scheduleId?: number) {
  return {
    name: policyData.name,
    scheduleId: scheduleId,
    monitoringType: policyData.monitoringType,
    externalApp: policyData.externalApp ?? null,
    requiredCheckOurTeam: policyData.requiredCheckOurTeam,
    requiredCheckNotOurTeam: policyData.requiredCheckNotOurTeam,
    checkMethodOurTeam: policyData.checkMethodOurTeam ?? null,
    checkMethodNotOurTeam: policyData.checkMethodNotOurTeam ?? null,
    settings: buildPolicySettings(policyData),
    /*
     * F2 (§1.5/§1.6) — seção "Registro de Atividade · Jornada" no TOPO do payload,
     * que é onde `Create/UpdateDSAttendancePolicyDto` a declara. Sem estes três
     * campos a seção não salvava nada: o formulário os montava e eles morriam aqui.
     * `null` (e não `undefined`) nas listas porque o DTO lê `null` como "usa o
     * default do produto", enquanto um campo ausente do JSON é "não mudou".
     */
    activityRecordsEnabled: policyData.activityRecordsEnabled ?? false,
    acceptedActivityTypes: policyData.acceptedActivityTypes ?? null,
    sourcePrecedence: policyData.sourcePrecedence ?? null,
    ...buildPostStationGate(policyData),
    links: (policyData.links ?? []).map((link) => ({
      startDate: link.startDate,
      endDate: link.endDate
    })),
    rules: sanitizePolicyRules(policyData),
    templates: hasRulesAndTemplates(policyData.monitoringType) ? (policyData.templates ?? []) : []
  }
}

/**
 * Guard de corrida de `registration-settings`: só a resposta do fetch mais recente
 * escreve estado. Escopo de módulo para ser monotônico entre instâncias do pinia
 * (o mesmo padrão de `useCheckValidationStore`).
 *
 * Mecanismo: cada fetch incrementa fetchId (const fetchId = ++registrationSettingsFetchId).
 * Ao chegar a resposta, compara com registrationSettingsFetchId: se diferente, descarta
 * (fetch antigo foi sobrescrito por novo). Assim evita condição de corrida onde resp. lenta
 * sobrescreve resp. rápida com dados stale (ex: usuario muda de escala, fetch antigo chega
 * atrasado e entrega config da escala A na escala B).
 */
let registrationSettingsFetchId = 0

/**
 * DEV-QA (PR #2839) — mesma trava para a fila e os contadores: trocar de aba e de filtro em
 * sequência rápida dispara buscas concorrentes, e só a MAIS RECENTE pode escrever estado.
 */
let containersFetchId = 0
let countsByKindFetchId = 0

// Definindo a interface para o estado da Store
interface DsAttendanceState {
  isLoadingPolicies: boolean
  policies: DSAttendancePolicyData[]
  loadingSwitches: Set<number>
  isLoadingContainers: boolean
  containers: DSAttendanceContainer[]
  totalContainers: number
  summary: { status: DSAttendanceStatus; count: number }[]
  containerFilters: ListContainersByCompanyParams
  listQueryParams: Record<string, string> | null
  containersLoadError: string | null
  policiesLoadError: string | null
  /**
   * Combos da fila. Ficam AQUI, e não em `useHealthUnitStore`/`useScheduleStore`, porque
   * `listHealthUnits`/`listSchedules` daquelas stores são estado global sobrescrito por outras
   * telas — o vazamento descrito em `FM-HEALTH-UNIT-LISTING-001`. A fila passa a ser dona do que
   * ela mesma oferece.
   */
  filterHealthUnits: DSAttendanceHealthUnitFilterOption[]
  filterSchedules: DSAttendanceScheduleFilterOption[]
  filterOptionsLoadError: string | null
  isLoadingFilterOptions: boolean
  /**
   * F2 — aba da fila unificada. `null` é "Todos", e é o default de propósito: no
   * dia do deploy não existe container `ATIVIDADE` e a fila tem de estar idêntica.
   */
  kindFilter: ContainerKind | null
  activityTypeFilter: ActivityType | null
  /** `null` = o backend ainda não serve `counts-by-kind`; as abas ficam sem número. */
  counts: ContainerCountsByKind | null
  /** Sempre preenchido: nasce no default seguro e só melhora com a resposta real. */
  registrationSettings: RegistrationSettings
  /**
   * Escala a que `registrationSettings` pertence (`null` = consulta global, sem
   * escala). A config é POR ESCALA e o slot é único: sem este escopo declarado,
   * quem lê não tem como saber se está olhando a config da escala certa.
   */
  registrationSettingsScheduleId: number | null
  isLoadingRegistrationSettings: boolean
  /**
   * O backend já recusou (403 `ACTIVITY_POLICY_SECTION_FORBIDDEN`) um salvamento
   * da seção de registro/auditoria. É a ÚNICA prova de que este usuário não pode
   * editar a seção "Auditoria" — a permissão da Q31 não a governa (C3).
   */
  isRegistrationSectionForbidden: boolean
}

export const useDsAttendanceStore = defineStore('dsAttendance', {
  state: (): DsAttendanceState => ({
    // Políticas
    isLoadingPolicies: false,
    policies: [],
    loadingSwitches: new Set<number>(),

    // Operação / Containers
    isLoadingContainers: false,
    containers: [],
    totalContainers: 0,
    summary: [],

    // Filtros Iniciais
    containerFilters: {
      healthUnitIds: [],
      scheduleIds: [],
      shiftIds: [],
      status: [],
      startDate: null,
      endDate: null,
      page: 1,
      limit: 10,
      overdue: false
    },
    listQueryParams: null,

    // Erros de leitura expostos para a view renderizar estado de erro + retry
    // (rule `error-handling.md`) — antes as falhas morriam num `console.error`.
    containersLoadError: null,
    policiesLoadError: null,
    filterHealthUnits: [],
    filterSchedules: [],
    filterOptionsLoadError: null,
    isLoadingFilterOptions: false,

    // F2 — Registro de Atividade
    kindFilter: null,
    activityTypeFilter: null,
    counts: null,
    registrationSettings: createDefaultRegistrationSettings(),
    registrationSettingsScheduleId: null,
    isLoadingRegistrationSettings: false,
    isRegistrationSectionForbidden: false
  }),

  getters: {
    hasContainersLoadError: (state): boolean => state.containersLoadError !== null,
    hasPoliciesLoadError: (state): boolean => state.policiesLoadError !== null,
    hasFilterOptionsLoadError: (state): boolean => state.filterOptionsLoadError !== null,
    /**
     * F2/Q4 — a inserção manual de check-in/out está bloqueada. É a MESMA verdade
     * que aposenta as decisões da fila `check-validation`; quem precisa dela lê
     * daqui, nunca de uma flag de front.
     */
    isManualCheckBlocked: (state): boolean => state.registrationSettings.manualCheckBlockEnabled,
    /** True quando a escala consultada aceita Registro de Atividade. */
    canSendActivityRecord: (state): boolean => state.registrationSettings.activityRecordsEnabled,
    /**
     * Feature detection do backend da F2 (C3): só é `true` depois de
     * `registration-settings` responder 200 COM `activityRecordsEnabled`. Enquanto
     * for `false`, nada da fase pode mudar a tela — nem para habilitar, nem para
     * travar: o web deploya antes do backend.
     */
    isRegistrationBackendAvailable: (state): boolean =>
      state.registrationSettings.isBackendAvailable === true
  },

  actions: {
    /**
     * Carrega os combos Unidades / Escala pelos endpoints do PRÓPRIO ds-attendance.
     *
     * Antes a fila pedia `/healthUnits/list-view` e `GET /schedules`, de outros módulos: o receptor
     * puro do ds_attendance não tem papel em nenhum dos dois, tomava 403 e a store daquelas telas
     * devolvia `[]` em silêncio — combo "Nenhum resultado", indistinguível de "a empresa não tem
     * unidades" (FM-HEALTH-UNIT-LISTING-002).
     *
     * NÃO relança de propósito: quem chama é o `Promise.all` do `onMounted`, e rejeitar aqui
     * abortaria o resto da inicialização da tela. O erro fica visível em `filterOptionsLoadError`.
     */
    async fetchFilterOptions(): Promise<void> {
      this.isLoadingFilterOptions = true
      this.filterOptionsLoadError = null
      try {
        const [healthUnits, schedules] = await Promise.all([
          Network.dsAttendance.listFilterHealthUnits(),
          Network.dsAttendance.listFilterSchedules()
        ])
        this.filterHealthUnits = healthUnits
        this.filterSchedules = schedules
      } catch (error) {
        this.filterOptionsLoadError = getApiErrorMessage(
          error,
          'Não foi possível carregar os filtros de unidade e escala.'
        )
        this.filterHealthUnits = []
        this.filterSchedules = []
        console.error('Erro ao carregar os filtros da fila:', error)
      } finally {
        this.isLoadingFilterOptions = false
      }
    },

    /**
     * Busca os containers usando os filtros estritamente tipados
     */
    async fetchContainersByCompany(): Promise<boolean> {
      const fetchId = ++containersFetchId
      this.isLoadingContainers = true
      this.containersLoadError = null
      try {
        const response = await Network.dsAttendance.listByCompany(this.containerFilters)
        // Resposta de uma busca já superada: não escreve nada (devolve `false`).
        if (fetchId !== containersFetchId) return false
        this.containers = response.data
        this.totalContainers = response.count

        // Transformação do resumo mantendo a tipagem do Enum
        if (response.summary) {
          this.summary = Object.entries(response.summary).map(([status, count]) => ({
            status: status as DSAttendanceStatus,
            count: Number(count)
          }))
        }
        return true
      } catch (error) {
        if (fetchId !== containersFetchId) return false
        // Sem isto a lista ficava com o resultado da busca anterior (ou vazia) e a
        // tela mostrava "Nenhum registro encontrado" para o que foi uma falha.
        this.containersLoadError = getApiErrorMessage(
          error,
          'Não foi possível carregar os registros de presença.'
        )
        this.containers = []
        this.totalContainers = 0
        this.summary = []
        console.error('Erro ao carregar containers:', error)
        throw error
      } finally {
        if (fetchId === containersFetchId) this.isLoadingContainers = false
      }
    },

    /**
     * Atualiza filtros sem usar 'any' no valor, respeitando a chave
     */
    setContainerFilter<K extends keyof ListContainersByCompanyParams>(
      key: K,
      value: ListContainersByCompanyParams[K]
    ): void {
      this.containerFilters[key] = value
    },

    async fetchPolicies(scheduleId: number | string): Promise<void> {
      this.isLoadingPolicies = true
      this.policiesLoadError = null
      try {
        this.policies = await Network.dsAttendance.listPolicies(scheduleId)
      } catch (error) {
        this.policiesLoadError = getApiErrorMessage(
          error,
          'Não foi possível carregar as políticas de registro de plantão.'
        )
        this.policies = []
        console.error('Erro ao carregar políticas:', error)
        throw error
      } finally {
        this.isLoadingPolicies = false
      }
    },

    /**
     * Salva ou atualiza políticas com mapeamento rigoroso de campos
     */
    async savePolicy(
      policyData: DSAttendancePolicyData,
      scheduleId?: number
    ): Promise<DSAttendancePolicyData> {
      this.isLoadingPolicies = true
      try {
        const payload = buildSavePolicyPayload(policyData, scheduleId)

        if (policyData.id) {
          return await Network.dsAttendance.updatePolicy(policyData.id, payload, scheduleId)
        } else {
          return await Network.dsAttendance.createPolicy(payload)
        }
      } finally {
        this.isLoadingPolicies = false
      }
    },

    resetPolicies() {
      this.policies = []
      this.policiesLoadError = null
    },

    async fetchPolicyById(
      policyId: number,
      scheduleId?: number | string
    ): Promise<DSAttendancePolicyData> {
      const data = await Network.dsAttendance.getPolicyById(policyId, scheduleId)
      return data
    },

    async togglePolicyStatus(
      policyId: number,
      isActive: boolean,
      scheduleId: number | string
    ): Promise<void> {
      this.loadingSwitches.add(policyId)
      try {
        await Network.dsAttendance.updateStatus(policyId, isActive, scheduleId)
        const policy = this.policies.find((p) => p.id === policyId)
        if (policy) policy.isActive = isActive
      } finally {
        this.loadingSwitches.delete(policyId)
      }
    },

    /* --------------------------------------------------------------------- *
     * F2 — Registro de Atividade
     * --------------------------------------------------------------------- */

    /**
     * Contagem por espécie para o segmented control. Nunca lança: uma falha de
     * contagem não pode derrubar a fila, que já carregou pelos filtros.
     */
    async fetchCountsByKind(): Promise<void> {
      const fetchId = ++countsByKindFetchId
      try {
        const counts = await Network.dsAttendance.getCountsByKind(this.containerFilters)
        if (fetchId !== countsByKindFetchId) return
        this.counts = counts
      } catch (error) {
        if (fetchId !== countsByKindFetchId) return
        this.counts = null
        console.debug('Não foi possível contar os registros por espécie:', error)
      }
    },

    /**
     * Config de registro do backend. Nunca lança e nunca deixa o estado indefinido:
     * uma falha volta ao default seguro, que é o comportamento de hoje.
     *
     * O slot é único para uma configuração POR ESCALA e os chamadores têm escopos
     * diferentes (detalhes do plantão passam `scheduleId`; fila e hub não). Duas
     * travas (I1): trocar de escopo zera o estado no ato — a config da escala A não
     * pode habilitar "Enviar registro" na B nem por um frame — e só a resposta do
     * fetch MAIS RECENTE escreve estado.
     */
    async fetchRegistrationSettings(scheduleId?: number): Promise<void> {
      const scope = scheduleId ?? null
      const fetchId = ++registrationSettingsFetchId

      if (scope !== this.registrationSettingsScheduleId) {
        this.registrationSettingsScheduleId = scope
        this.registrationSettings = createDefaultRegistrationSettings()
      }

      this.isLoadingRegistrationSettings = true
      try {
        const settings = await Network.dsAttendance.getRegistrationSettings(scheduleId)
        if (fetchId !== registrationSettingsFetchId) return
        this.registrationSettings = settings
      } catch (error) {
        if (fetchId !== registrationSettingsFetchId) return
        this.registrationSettings = createDefaultRegistrationSettings()
        console.debug('Não foi possível carregar as configurações de registro:', error)
      } finally {
        if (fetchId === registrationSettingsFetchId) this.isLoadingRegistrationSettings = false
      }
    },

    /**
     * O backend recusou o salvamento da seção de registro/auditoria (§1.6). A partir
     * daqui a seção "Auditoria" fica em leitura para este usuário — antes disso NÃO,
     * porque a permissão da Q31 não a governa e ela já está em produção (C3).
     */
    markRegistrationSectionForbidden(): void {
      this.isRegistrationSectionForbidden = true
    },

    /**
     * Envio do Registro de Atividade. O erro sobe CRU de propósito: a tela escolhe
     * o texto pelo `code` do backend (§2.7), e embrulhá-lo aqui apagaria o código.
     * Loading state é gerenciado pelo caller (SendRecordModal).
     */
    async submitActivityRecord(formData: FormData): Promise<ActivityRecordCreatedResponse> {
      return Network.dsAttendance.createActivityRecord(formData)
    },

    /**
     * Simulação do efetivo antes de o auditor gravar. Devolve o envelope de §3.4 e
     * `null` quando a rota não está no ar (B17 §1.1 rota 5).
     */
    async fetchEffectivePreview(
      containerId: number | string,
      params: { checkin?: string | null; checkout?: string | null }
    ): Promise<EffectivePreviewEnvelope | null> {
      return Network.dsAttendance.getEffectivePreview(containerId, params)
    },

    /** Troca a aba da fila; limpar `kind` também limpa o filtro de tipo. */
    setKindFilter(kind: ContainerKind | null): void {
      this.kindFilter = kind
      if (kind !== ContainerKind.ATIVIDADE) this.activityTypeFilter = null
      this.containerFilters.kind = kind ?? undefined
      this.containerFilters.activityType = this.activityTypeFilter ?? undefined
    },

    setActivityTypeFilter(activityType: ActivityType | null): void {
      this.activityTypeFilter = activityType
      this.containerFilters.activityType = activityType ?? undefined
    }
  }
})
